import dayjs from "dayjs";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import DeleteWithConfirm from "./DeleteWithConfirm";
import { getDbtIntegrationName, getInstanceName } from "@/helpers/utils";
import { AccessRuleTableRowData } from "./UserGroupDetails.types";
import { DATASTORE_ENTITY_TYPE } from "./accessRules.constants";
import { Datastore } from "@/pages/datastores/types";
import { DbtModelFiltersResponse } from "@/types/dbtModels";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";

interface AccessRulesGroupedTableProps {
  items: AccessRuleTableRowData[];
  datastores?: Datastore[];
  dbtIntegrations?: DbtModelFiltersResponse["dbt_integrations"];
  expandedGroups: Record<string, boolean>;
  toggleGroup: (groupName: string) => void;
  getUserName: (userId: string) => string;
  canManageRules: boolean;
  groupId?: string;
  refetch: () => void;
}

const AccessRulesGroupedTable = ({
  items,
  datastores,
  dbtIntegrations,
  expandedGroups,
  toggleGroup,
  getUserName,
  canManageRules,
  groupId,
  refetch,
}: AccessRulesGroupedTableProps) => (
  <DataTable<AccessRuleTableRowData>
    onRowClick={item => {
      if ("isGroupHeader" in item && item.isGroupHeader && Number(item.count) > 1) {
        toggleGroup(item.grouping || "ungrouped");
      }
    }}
    data={{ items }}
    header={[
      {
        id: 0,
        uid: "rule",
        label: "Rule",
        render: item => <div>{item.ruleOperator || ""}</div>,
      },
      {
        id: 1,
        uid: "entity_type",
        label: "Entity Type",
        render: item => {
          const isIndented = item.ruleOperator === "AND";
          if ("isGroupHeader" in item && item.isGroupHeader) {
            const groupKey = item.grouping || "ungrouped";
            const isExpanded = expandedGroups[groupKey];
            return (
              <Stack direction="row" align="center">
                <div>
                  {item.entity_type === DATASTORE_ENTITY_TYPE ? "instance" : item.entity_type}
                </div>
                {Number(item.count) > 1 && (
                  <Stack
                    direction="row"
                    align="center"
                    className="al-ml-2 al-text-gray-500"
                    aria-label={isExpanded ? "Collapse group" : "Expand group"}
                  >
                    {isExpanded ? (
                      <IconChevronUp size={16} aria-hidden="true" />
                    ) : (
                      <IconChevronDown size={16} aria-hidden="true" />
                    )}
                  </Stack>
                )}
              </Stack>
            );
          }
          return (
            <div className={isIndented ? "al-pl-6" : ""}>
              {item.entity_type === DATASTORE_ENTITY_TYPE ? "instance" : item.entity_type}
            </div>
          );
        },
      },
      {
        id: 2,
        uid: "match_type",
        label: "Match Type",
        render: item => <div>{item.rule.type}</div>,
      },
      {
        id: 3,
        uid: "rule_entity",
        label: "Rule text",
        render: item => {
          if (item.entity_type === DATASTORE_ENTITY_TYPE) {
            return (
              <div className="al-break-words">
                {getInstanceName(item.rule.entity, datastores ?? []) || item.rule.entity}
              </div>
            );
          }
          if (item.entity_type === "dbt_integration") {
            return (
              <div className="al-break-words">
                {getDbtIntegrationName(item.rule.entity, dbtIntegrations) || item.rule.entity}
              </div>
            );
          }
          return <div className="al-break-words">{item.rule.entity}</div>;
        },
      },
      {
        id: 4,
        uid: "created",
        label: "Created",
        render: item => (
          <div>
            <div>
              <b>{getUserName(item.created_by)}</b>
            </div>
            <div>{dayjs(item.created_at).format("DD MMM YYYY hh:mm")}</div>
          </div>
        ),
      },
      {
        id: 5,
        uid: "updated",
        label: "Updated",
        render: item => (
          <div>
            <div>
              <b>{getUserName(item.updated_by)}</b>
            </div>
            <div>{dayjs(item.updated_at).format("DD MMM YYYY hh:mm")}</div>
          </div>
        ),
      },
      {
        id: 6,
        uid: "actions",
        label: "Actions",
        render: item => (
          <div>
            {canManageRules ? (
              <DeleteWithConfirm
                entityId={item.id}
                id={groupId || ""}
                refetch={refetch}
                entityType="rule"
                title={`Delete rule ${item.rule.entity}`}
              />
            ) : null}
          </div>
        ),
      },
    ]}
  />
);

export default AccessRulesGroupedTable;
